
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CareerCounselor, CAREER_COUNSELOR_ID } from '@/hooks/useCareerCounselors';
import { saveToSessionStorage } from '@/utils/storage/sessionStorageService';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface CounselorCardProps {
  counselor: CareerCounselor;
}

const CounselorCard: React.FC<CounselorCardProps> = ({ counselor }) => {
  const { isAuthenticated } = useCentralizedAuth();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const navigate = useNavigate();
  
  const handleBookSession = () => {
    if (!isAuthenticated) {
      setShowLoginDialog(true);
      return;
    }
    
    if (!CAREER_COUNSELOR_ID) {
      console.error('Career Counselor ID not initialized yet. Please try again in a moment.');
      toast.error('Service temporarily unavailable. Please try again in a few moments.');
      return;
    }
    
    try {
      // Save counselor data to session storage for booking flow
      const storageKey = `counselor_${counselor.id}`;
      
      // Ensure the counselor has the association_id
      const counselorWithAssociation = {
        ...counselor,
        association_id: CAREER_COUNSELOR_ID
      };
      
      console.log('Saving counselor with association ID:', counselorWithAssociation);
      saveToSessionStorage(storageKey, counselorWithAssociation);
      
      // Navigate to the booking page
      navigate(`/partners/booking-dialog`, {
        state: {
          partner: {
            id: counselor.id,
            name: counselor.name,
            profile_image: counselor.profile_image,
            associations: [{ id: CAREER_COUNSELOR_ID, name: 'Career Counselor' }]
          },
          pricing: {
            id: counselor.pricing_id,
            session_charge: counselor.session_charge,
            min_session_time: counselor.min_session_time,
            currency: counselor.currency,
            association_id: CAREER_COUNSELOR_ID
          },
          associationType: 'Career Counselor',
          storageKey
        }
      });
    } catch (error) {
      console.error('Error preparing booking process:', error);
      toast.error('Unable to process your booking request. Please try again.');
    }
  };

  const handleLoginRedirect = () => {
    setShowLoginDialog(false);
    // Store the redirect path for after login
    sessionStorage.setItem('redirectAfterLogin', '/career-counselors');
    navigate('/login');
  };

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage 
                src={counselor.profile_image} 
                alt={counselor.name} 
              />
              <AvatarFallback className="text-xl font-semibold">
                {counselor.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-lg mb-1">{counselor.name}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {counselor.education || 'Education not specified'}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2 flex-grow flex flex-col">
          <div className="mb-4 flex-grow">
            <div className="flex gap-1 mb-2">
              <span className="text-sm font-medium">Experience:</span>
              <span className="text-sm">{counselor.experience || 0} years</span>
            </div>
            {counselor.about_us && (
              <p className="text-sm line-clamp-3">{counselor.about_us}</p>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-auto pt-4 border-t">
            <div>
              <div className="text-sm">
                {counselor.min_session_time} min session
              </div>
              <div className="font-bold">
                {counselor.session_charge} {counselor.currency}
              </div>
            </div>
            <Button onClick={handleBookSession}>Book Now</Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Login Required</DialogTitle>
          </DialogHeader>
          <p className="py-4">Please login to book a session with our counselors.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoginDialog(false)}>Cancel</Button>
            <Button onClick={handleLoginRedirect}>Login</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CounselorCard;
